import { useRef, useState, useCallback, useMemo } from "react";
import EmailEditor from "react-email-editor";
import { Button } from "@/components/ui/button";
import { Loader2, Eye, Save } from "lucide-react";
import { toast } from "sonner";

/**
 * Thin wrapper around Unlayer's React email editor.
 *
 * Props:
 *  - initialDesign: optional Unlayer JSON to preload (e.g. when editing)
 *  - onSave(design, html): called when the user clicks "Use this design"
 *  - onCancel(): close the builder
 *  - businessName: prefills brand context
 */
export default function EmailBuilder({ initialDesign, onSave, onCancel, businessName }) {
  const editorRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [exporting, setExporting] = useState(false);

  const onReady = useCallback(() => {
    setReady(true);
    if (initialDesign && editorRef.current?.editor) {
      try { editorRef.current.editor.loadDesign(initialDesign); } catch (e) { console.warn("Unlayer loadDesign failed:", e); }
    }
  }, [initialDesign]);

  const editorOptions = useMemo(() => ({
    displayMode: "email",
    features: {
      preview: true,
      imageEditor: true,
      undoRedo: true,
      stockImages: false,
    },
    tools: {
      form: { enabled: false },
      menu: { enabled: false },
    },
    mergeTags: businessName ? { business_name: { name: "Business name", value: businessName } } : {},
    appearance: {
      theme: "modern_light",
      panels: {
        tools: { dock: "right" },
      },
    },
  }), [businessName]);

  const exportAndSave = () => {
    if (!editorRef.current?.editor) return;
    setExporting(true);
    editorRef.current.editor.exportHtml((data) => {
      const { design, html } = data || {};
      setExporting(false);
      if (!html || html.length < 100) {
        toast.error("Add at least one block before saving.");
        return;
      }
      onSave?.(design, html);
    });
  };

  const preview = () => {
    if (!editorRef.current?.editor) return;
    editorRef.current.editor.showPreview("desktop");
  };

  return (
    <div className="rounded-md border border-border bg-white overflow-hidden" data-testid="email-builder">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3 flex-wrap bg-gray-50/60">
        <div>
          <p className="text-sm font-medium">Email builder</p>
          <p className="text-[11px] text-gray-500">Drag blocks from the right palette. Branded as <strong>{businessName || "your business"}</strong>.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={preview} disabled={!ready} className="rounded-[35px]" data-testid="email-builder-preview">
            <Eye size={13} className="mr-1.5" /> Preview
          </Button>
          <Button variant="ghost" size="sm" onClick={onCancel} className="rounded-[35px]" data-testid="email-builder-cancel">
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={exportAndSave}
            disabled={!ready || exporting}
            className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] shadow-sm hover:shadow-md"
            data-testid="email-builder-save"
          >
            {exporting ? <><Loader2 size={13} className="mr-1.5 animate-spin" /> Saving…</> : <><Save size={13} className="mr-1.5" /> Use this design</>}
          </Button>
        </div>
      </div>
      <div className="relative" style={{ height: 620 }}>
        {!ready && (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-500 z-10">
            <Loader2 size={16} className="animate-spin mr-2" /> Loading builder…
          </div>
        )}
        <EmailEditor
          ref={editorRef}
          onReady={onReady}
          minHeight="620px"
          options={editorOptions}
        />
      </div>
    </div>
  );
}
